/*
  AoA base (loaded with defer from layout/theme.liquid).
  - Adds the "aoa-js" class to <html> so CSS may hide [data-aoa-reveal] before it appears.
  - Fade-up with IntersectionObserver, as in the approved mockup: it repeats on every scroll down.
    An element that leaves through the bottom of the screen is re-armed (hidden, 28px lower, no
    transition) and plays again the next time it enters. What leaves through the top stays visible.
  - [data-aoa-reveal-items] (horizontal rails): the container does not move; its cards
    ([data-aoa-rail-track] > *) that are on screen fade up one by one (0.1s apart, up to 0.5s) with
    the Web Animations API. Cards off screen sideways are simply visible when swiped to.
  - Nothing happens with prefers-reduced-motion or without IntersectionObserver: content stays visible.
  Elements already on screen (or above it) when the script runs are marked visible right away,
  in the same task as the class, so there is no flash on first paint.
*/
(function () {
  'use strict';

  if (window.aoaBase) return;
  window.aoaBase = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

  var SELECTOR =
    '[data-aoa] [data-aoa-reveal], [data-aoa][data-aoa-reveal], [data-aoa] [data-aoa-reveal-items], [data-aoa][data-aoa-reveal-items]';
  var ITEMS = '[data-aoa-rail-track] > *';
  var REVEALED = 'data-aoa-revealed';
  var MAX_STAGGER = 5;
  var STAGGER_SECONDS = 0.1;
  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  // Duration and travel from snippets/aoa-tokens.liquid (--rd-dur-reveal, --rd-reveal-y)
  function motion(element) {
    var style = getComputedStyle(element);
    return {
      duration: (parseFloat(style.getPropertyValue('--rd-dur-reveal')) || 0.6) * 1000,
      y: style.getPropertyValue('--rd-reveal-y').trim() || '28px'
    };
  }

  function onScreenSideways(element) {
    var rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.right > 0 && rect.left < window.innerWidth;
  }

  function revealItems(container, animate) {
    if (!animate || !container.animate) return;
    var m = motion(container);
    var shown = 0;
    container.querySelectorAll(ITEMS).forEach(function (item) {
      if (!onScreenSideways(item)) return;
      item.animate(
        [
          { opacity: 0, transform: 'translateY(' + m.y + ')' },
          { opacity: 1, transform: 'none' }
        ],
        { duration: m.duration, delay: Math.min(shown, MAX_STAGGER) * STAGGER_SECONDS * 1000, easing: EASE, fill: 'backwards' }
      );
      shown += 1;
    });
  }

  function reveal(element, delayIndex, animate) {
    if (element.hasAttribute('data-aoa-reveal-items')) {
      element.setAttribute(REVEALED, '');
      revealItems(element, animate);
      return;
    }
    element.style.setProperty('--aoa-reveal-delay', Math.min(delayIndex, MAX_STAGGER) * STAGGER_SECONDS + 's');
    element.setAttribute(REVEALED, '');
  }

  // Removing the attribute hides at once: the transition only exists while revealed (assets/aoa-base.css)
  function rearm(element) {
    element.removeAttribute(REVEALED);
    if (element.hasAttribute('data-aoa-reveal-items') && element.getAnimations) {
      element.getAnimations({ subtree: true }).forEach(function (animation) {
        animation.cancel();
      });
    }
  }

  // Shows what enters through the bottom 10% of the screen
  var revealObserver = new IntersectionObserver(
    function (entries) {
      var shown = 0;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.target.hasAttribute(REVEALED)) return;
        reveal(entry.target, shown, true);
        shown += 1;
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  // Re-arms what is completely below the screen again. Checked on scroll (once per frame, reads
  // only) and not with a second observer: a jump from below to above the screen (back to top) does
  // not change the intersection, so an observer would never report it.
  var watched = [];
  var ticking = false;
  function checkRearm() {
    ticking = false;
    var viewportHeight = window.innerHeight;
    watched.forEach(function (element) {
      if (element.hasAttribute(REVEALED) && element.getBoundingClientRect().top >= viewportHeight) rearm(element);
    });
  }
  window.addEventListener(
    'scroll',
    function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(checkRearm);
    },
    { passive: true }
  );

  function watch(scope) {
    var viewportHeight = window.innerHeight;
    scope.querySelectorAll(SELECTOR).forEach(function (element) {
      if (element.aoaRevealWatched) return;
      element.aoaRevealWatched = true;
      // Hidden (display: none) or already reached: show without animation
      if (element.getBoundingClientRect().top < viewportHeight) reveal(element, 0, false);
      revealObserver.observe(element);
      watched.push(element);
    });
    // Theme editor: drop the elements of a section that was re-rendered
    watched = watched.filter(function (element) {
      return element.isConnected;
    });
  }

  watch(document);
  document.documentElement.classList.add('aoa-js');

  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', function (event) {
    watch(event.target);
  });
})();
